/**
 * Workspace shell (doc 147 §5.3 redesign — task #8).
 *
 * The unified canvas: tree (in `App.tsx` chrome) + filtered graph + rail/sheet
 * (added by tasks #10/#12). Mounts Sigma when WebGL is available; falls back
 * to a grouped text list otherwise (PERF-05 graceful degradation).
 *
 * Click semantics:
 *   - Click a node → onFocus(id) — the parent translates to a workspace route
 *     change (`#workspace/Holding`), which re-enters this view in `focused`
 *     mode.
 *   - Click the stage → onClearFocus() — back to idle.
 *
 * Ambient motion runs while `workspaceMode.kind === 'idle'` and stops when an
 * entity is focused (the eye should rest on the focal node, not chase drift).
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { AtlasManifest, DomainLayer } from '@atlas-manifest/types';
import { buildGraph, type LayoutCoords } from '../graph/buildGraph.js';
import { buildTerritoryLayout } from '../graph/territoryLayout.js';
import { buildConstellationSvg } from '../graph/exportGraph.js';
import type { Mode, GraphContrast } from '../theme/tokens.js';
import type { ViewMode } from '../state/store.js';
import { detectWebGL } from '../graph/webglSupport.js';
import type { ConstellationController } from '../graph/render.js';
import {
  applyFilters,
  defaultFilters,
  type ConstellationFilters as Filters,
} from '../state/filters.js';
import {
  domainsSorted,
  entitiesInDomain,
} from '../state/selectors.js';
import { DomainBadge } from './DomainBadge.js';
import { LayerBadge } from './LayerBadge.js';
import {
  focusedEntityId,
  type WorkspaceMode,
} from '../state/workspaceMachine.js';
import {
  walkNeighborByDirection,
  entityForDomainIndex,
} from '../state/neighborWalk.js';
import { mapKey, type KeyContext } from '../state/keyboardMap.js';
import { ConstellationFilters } from './ConstellationFilters.js';
// Lazy — only renders alongside the 3D Canvas, so it can ride that chunk
// rather than the always-loaded initial bundle (which is tight at 250 KB).
const DomainLegend = lazy(() =>
  import('./DomainLegend.js').then((m) => ({ default: m.DomainLegend })),
);
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { Spinner } from '../components/Spinner.js';
import { WorkspaceRail } from './WorkspaceRail.js';
import { HoverPreview } from './HoverPreview.js';
import { PersonaSlicerDialog } from './PersonaSlicerDialog.js';

// Lazy chunk — three.js + R3F + drei (~140 KB gzipped) only loaded when the
// user toggles into 3D, keeping the initial bundle under the §8.7 budget.
const ConstellationCanvas3d = lazy(() => import('../graph3d/ConstellationCanvas3d.js'));
import { ConstellationMirror } from '../graph3d/ConstellationMirror.js';
import { RendererErrorBoundary } from '../components/RendererErrorBoundary.js';
import { IDLE_TEARDOWN_MS } from '../graph3d/idleTeardown.js';
import { ViewHeader } from '../components/ViewHeader.js';

export interface WorkspaceViewProps {
  manifest: AtlasManifest;
  layout: LayoutCoords;
  mode: Mode;
  graphContrast: GraphContrast;
  workspaceMode: WorkspaceMode;
  pinned: string[];
  /** 2D Sigma view or 3D R3F view (lazy-loaded). */
  view: ViewMode;
  onFocus: (entityId: string) => void;
  onClearFocus: () => void;
  onOpenSheet: () => void;
  onPopOutSheet: () => void;
  onCopyLink: () => void;
  onTogglePin: (entityId: string) => void;
  onToggleView: () => void;
}

/** WebGL-free fallback list — domain-grouped, click-to-focus. */
function TextFallback({
  manifest,
  visible,
  focusedId,
  onFocus,
}: {
  manifest: AtlasManifest;
  visible: Set<string>;
  focusedId: string | undefined;
  onFocus: (entityId: string) => void;
}): React.JSX.Element {
  const domains = domainsSorted(manifest).filter((d) =>
    entitiesInDomain(manifest, d.id).some((e) => visible.has(e.id)),
  );

  return (
    <div className="constellation-fallback">
      <div className="banner" role="status">
        <Icon name="visibility_off" size="sm" />
        <span>WebGL unavailable — showing the text view of the model.</span>
      </div>
      {domains.length === 0 ? (
        <EmptyState icon="filter_list" title="No entities match the current filters" />
      ) : (
        domains.map((domain) => (
          <section key={domain.id} style={{ marginTop: 'var(--sp-md)' }}>
            <div className="section-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
              <DomainBadge domainId={domain.id} size="sm" />
              <LayerBadge layer={(domain.layer as DomainLayer) ?? 'L5'} size="sm" />
            </div>
            {entitiesInDomain(manifest, domain.id)
              .filter((e) => visible.has(e.id))
              .map((entity) => (
                <button
                  key={entity.id}
                  className="ref-row"
                  aria-current={entity.id === focusedId ? 'true' : undefined}
                  onClick={() => onFocus(entity.id)}
                >
                  <Icon name="scatter_plot" size="sm" />
                  <span className="mono">{entity.name}</span>
                </button>
              ))}
          </section>
        ))
      )}
    </div>
  );
}

export function WorkspaceView({
  manifest,
  layout,
  mode,
  graphContrast,
  workspaceMode,
  pinned,
  view,
  onFocus,
  onClearFocus,
  onOpenSheet,
  onPopOutSheet,
  onCopyLink,
  onTogglePin,
  onToggleView,
}: WorkspaceViewProps): React.JSX.Element {
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const [slicerOpen, setSlicerOpen] = useState(false);
  // Keep-alive trigger (PR M): the 3D Canvas is lazy-loaded on first
  // `view === '3d'` and then stays mounted, so subsequent toggles are
  // CSS-only — no unmount race, no chunk re-route, instant flip.
  const [has3dEverActivated, setHas3dEverActivated] = useState(view === '3d');
  useEffect(() => {
    if (view === '3d') setHas3dEverActivated(true);
  }, [view]);

  // Idle-teardown (PR N): if 3D has been hidden for IDLE_TEARDOWN_MS, fully
  // unmount the Canvas to release its GL context + GPU buffers (~10–50 MB).
  // Next toggle to 3D re-mounts cleanly. A continuous timer reset on every
  // 3D visit means rapid toggles never trip the teardown.
  useEffect(() => {
    if (!has3dEverActivated || view === '3d') return;
    const t = window.setTimeout(() => {
      setHas3dEverActivated(false);
    }, IDLE_TEARDOWN_MS);
    return () => window.clearTimeout(t);
  }, [view, has3dEverActivated]);
  // Keep-alive trigger for 2D (mirrors has3dEverActivated): Sigma only mounts
  // when the 2D view is first activated, guaranteeing the container is visible
  // (not display:none) at construction time. No idle teardown for 2D — Sigma
  // is quiescent when idle and holds far less GPU memory than R3F.
  const [has2dEverActivated, setHas2dEverActivated] = useState(view === '2d');
  useEffect(() => {
    if (view === '2d') setHas2dEverActivated(true);
  }, [view]);
  const webglAvailable = useMemo(() => detectWebGL(), []);
  const focusedId = focusedEntityId(workspaceMode);

  // Workspace-scoped keyboard dispatcher. Lives here (rather than App.tsx)
  // because it needs the controller ref for fit-focus and the manifest for
  // walk-neighbour / focus-domain. App.tsx keeps the global keys (search,
  // shortcuts). The two dispatchers act on disjoint action kinds.
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      const target = event.target as Element | null;
      const inputActive = isInputElement(target);
      const ctx: KeyContext = { workspaceMode, inputActive };
      const action = mapKey(event, ctx);
      if (!action) return;
      switch (action.kind) {
        case 'open-slicer':
          event.preventDefault();
          setSlicerOpen(true);
          return;
        case 'open-entity':
          if (workspaceMode.kind === 'focused') {
            event.preventDefault();
            onOpenSheet();
          }
          return;
        case 'escape':
          if (workspaceMode.kind === 'focused') {
            event.preventDefault();
            onClearFocus();
          }
          return;
        case 'walk-neighbor': {
          if (workspaceMode.kind === 'idle') return;
          const next = walkNeighborByDirection(
            manifest,
            layout,
            workspaceMode.entityId,
            action.direction,
          );
          if (next) {
            event.preventDefault();
            onFocus(next);
          }
          return;
        }
        case 'focus-domain': {
          const hub = entityForDomainIndex(manifest, action.index);
          if (hub) {
            event.preventDefault();
            onFocus(hub);
          }
          return;
        }
        case 'fit-focus':
          if (workspaceMode.kind !== 'idle' && controllerRef.current) {
            event.preventDefault();
            controllerRef.current.fitFocus(workspaceMode.entityId);
          }
          return;
        case 'toggle-pin':
          if (workspaceMode.kind !== 'idle') {
            event.preventDefault();
            onTogglePin(workspaceMode.entityId);
          }
          return;
        case 'copy-link':
          event.preventDefault();
          onCopyLink();
          return;
        case 'open-in-new-tab':
          if (workspaceMode.kind === 'focused') {
            event.preventDefault();
            onPopOutSheet();
          }
          return;
        default:
          return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    workspaceMode,
    manifest,
    layout,
    onFocus,
    onClearFocus,
    onOpenSheet,
    onPopOutSheet,
    onCopyLink,
    onTogglePin,
  ]);

  // Build the territory layout from the manifest's domain list (deterministic).
  const territories = useMemo(
    () => buildTerritoryLayout(manifest.domains.map((d) => d.id)),
    [manifest.domains],
  );
  // Rebuild the graph whenever mode (palette), layout, or manifest changes —
  // territories are folded into coords inside buildGraph.
  const graph = useMemo(
    () => buildGraph(manifest, layout, { mode, territories }),
    [manifest, layout, mode, territories],
  );
  const visible = useMemo(() => applyFilters(manifest, filters), [manifest, filters]);
  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<ConstellationController | null>(null);

  /* c8 ignore start — Sigma mount/teardown; needs a real WebGL context.
   *
   * Permanent-once-mounted lifecycle (PR M · keep-alive canvases). Sigma
   * mounts on first activation of the 2D view (has2dEverActivated gates
   * construction so the container is guaranteed visible, not display:none),
   * then stays alive across 2D ↔ 3D toggles — visibility is the only
   * thing that changes. Mirrors the has3dEverActivated pattern for R3F.
   * Modern browsers cap at 16 WebGL contexts/tab; Atlas uses 2.
   * The `view` dep is absent — both canvases stay alive once mounted. */
  useEffect(() => {
    if (!webglAvailable || !has2dEverActivated || !containerRef.current) return;
    let disposed = false;
    const container = containerRef.current;
    void import('../graph/render.js').then(({ renderConstellation }) => {
      if (disposed) return;
      controllerRef.current = renderConstellation(container, graph, {
        onNodeClick: (nodeId) => {
          if (nodeId) onFocus(nodeId);
          else onClearFocus();
        },
        onNodeHover: (nodeId, x, y) => {
          if (nodeId) setHover({ id: nodeId, x, y });
          else setHover(null);
        },
      });
    });
    return () => {
      disposed = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [webglAvailable, has2dEverActivated, graph, onFocus, onClearFocus]);

  useEffect(() => {
    controllerRef.current?.setVisible(visible.size === manifest.entities.length ? null : visible);
  }, [visible, manifest.entities.length]);

  useEffect(() => {
    controllerRef.current?.setFocus(focusedId ?? null);
  }, [focusedId]);

  // Re-paint when the contrast var swaps.
  useEffect(() => {
    controllerRef.current?.refresh();
  }, [graphContrast]);

  const exportSvg = (): void => {
    const svg = buildConstellationSvg(graph, { mode });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'atlas-constellation.svg';
    a.click();
    URL.revokeObjectURL(url);
  };
  /* c8 ignore stop */

  return (
    <div className="workspace-root">
      <ViewHeader
        eyebrow="WORKSPACE"
        title="Constellation Explorer"
        subtitle="Entity graph · impact tracing · field paths"
      />
      <div className="constellation">
        <ConstellationFilters
        manifest={manifest}
        filters={filters}
        onChange={setFilters}
        visibleCount={visible.size}
      />
      <div className="filter-bar">
        {focusedId && (
          <button
            className="btn btn-ghost"
            onClick={onClearFocus}
            aria-label="Clear focus"
          >
            <Icon name="close" size="sm" />
            Clear focus
          </button>
        )}
        <button
          className={`btn btn-ghost workspace-view-toggle${view === '3d' ? ' is-active' : ''}`}
          onClick={onToggleView}
          aria-label={view === '3d' ? 'Switch to 2D constellation' : 'Switch to 3D constellation'}
          aria-pressed={view === '3d'}
        >
          <Icon name={view === '3d' ? 'view_in_ar' : 'grid_on'} size="sm" />
          {view === '3d' ? '3D' : '2D'}
        </button>
        <button className="btn btn-ghost" onClick={exportSvg} aria-label="Export constellation as SVG">
          <Icon name="file_download" size="sm" />
          Export SVG
        </button>
      </div>
      <div className="workspace-split">
        {/*
          Keep-alive canvases (PR M). Both Sigma (2D) and the R3F Canvas (3D)
          are mounted permanently once activated; only their *visibility*
          flips on toggle. This avoids the dev-StrictMode unmount race with
          R3F's imperative canvas DOM at the cost of one always-alive WebGL
          context. The 3D Canvas is still lazy-activated on first toggle so
          the initial bundle stays under budget.

          When hidden, the R3F Canvas runs at `frameloop="never"` so it
          consumes zero per-frame CPU/GPU. Sigma's renderer is naturally
          quiescent when not interacted with.
        */}
        {webglAvailable ? (
          <>
            {/* 2D Sigma — permanent once mounted. */}
            <div
              ref={containerRef}
              className="constellation-canvas"
              role="img"
              aria-label={`Constellation graph — ${visible.size} entities${focusedId ? `, focused on ${focusedId}` : ''}`}
              style={{ display: view === '2d' ? undefined : 'none' }}
            />

            {/* 3D R3F — lazy-mounted on first 3D toggle, then permanent. */}
            {has3dEverActivated && (
              <div
                className="constellation-canvas-3d"
                role="img"
                aria-label={`3D constellation — ${visible.size} entities${focusedId ? `, focused on ${focusedId}` : ''}`}
                style={{ display: view === '3d' ? undefined : 'none' }}
              >
                <RendererErrorBoundary
                  fallback={
                    <div className="constellation-3d-loading">
                      3D renderer unavailable — using the screen-reader fallback.
                    </div>
                  }
                >
                  <Suspense fallback={<Spinner label="Loading 3D scene" />}>
                    <ConstellationCanvas3d
                      manifest={manifest}
                      focusedId={focusedId}
                      onFocus={onFocus}
                      onClearFocus={onClearFocus}
                      // Pause R3F's render loop entirely when the 3D canvas
                      // is hidden — no per-frame cost while 2D is on screen.
                      frameloop={view === '3d' ? 'always' : 'never'}
                      // Sync 3D visibility with the workspace filter so the
                      // chrome controls actually scope the scene. `null`-when-
                      // unfiltered keeps the renderer's "skip nothing" fast path.
                      {...(visible.size === manifest.entities.length ? {} : { visible })}
                    />
                  </Suspense>
                </RendererErrorBoundary>
                {/* HTML overlay — outside the WebGL Canvas so click events
                    don't fight the OrbitControls drag-to-rotate gesture. */}
                <DomainLegend
                  manifest={manifest}
                  filters={filters}
                  visible={visible}
                  onChange={setFilters}
                />
                <ConstellationMirror
                  manifest={manifest}
                  focusedId={focusedId}
                  onFocus={onFocus}
                />
              </div>
            )}
          </>
        ) : (
          <TextFallback
            manifest={manifest}
            visible={visible}
            focusedId={focusedId}
            onFocus={onFocus}
          />
        )}
        {workspaceMode.kind === 'focused' && (
          <WorkspaceRail
            manifest={manifest}
            entityId={workspaceMode.entityId}
            pinned={pinned}
            onOpenSheet={onOpenSheet}
            onTogglePin={onTogglePin}
            onNavigateEntity={onFocus}
          />
        )}
      </div>
      {hover && (
        <HoverPreview
          manifest={manifest}
          entityId={hover.id}
          x={hover.x}
          y={hover.y}
        />
      )}
      {slicerOpen && (
        <PersonaSlicerDialog
          manifest={manifest}
          filters={filters}
          onChange={setFilters}
          onClose={() => setSlicerOpen(false)}
        />
      )}
      </div>
    </div>
  );
}

function isInputElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.getAttribute('contenteditable') === 'true';
}
